import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FaDownload, FaFilePdf } from 'react-icons/fa';
import { IOfferOverview, IOfferPricingAddon, IOfferPricingItem } from '../../model/offer/OfferOverview';
import { OfferPdfTypeEnum } from '../../model/offer/OfferPdf';
import { downloadFileRequest } from '../../api/api.service';
import { formatDate } from '../../utils/Functions';
import { priceSplitter } from '../../utils/helpers/NumberFormater';
import { ErrorToast } from '../../utils/toasters';
import SingleSelectDropdown from './SingleSelectDropdown';

interface IOverviewModal {
    show: boolean;
    id?: number | null;
    title: string;
    fetchOverview: (id: number) => Promise<IOfferOverview>;
    storedPdfUrl: (pdfId: number) => string;
    generatedPdfUrl: (id: number) => string;
    onHide: () => void;
}

const formatPrice = (value?: number | null) =>
    value === null || value === undefined ? '-' : `€ ${priceSplitter(Math.round(value))}`;

const ReadOnlyField = ({ label, value }: { label: string; value?: string | number | null }) => (
    <Form.Group className="mb-3">
        <Form.Label className="fs-8 text-muted mb-1">{label}</Form.Label>
        <Form.Control size="sm" readOnly value={value ?? ''} placeholder="-" />
    </Form.Group>
);

const Info = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="mb-3">
        <div className="fs-8 text-muted">{label}</div>
        <div>{children}</div>
    </div>
);

const getAddonDescription = (addon: IOfferPricingAddon) =>
    [addon.detail, addon.duration !== '-' ? addon.duration : null, addon.location !== '-' ? addon.location : null]
        .filter(Boolean)
        .join(' · ');

export function OverviewModal({
    show,
    id,
    title,
    fetchOverview,
    storedPdfUrl,
    generatedPdfUrl,
    onHide,
}: IOverviewModal) {
    const [overview, setOverview] = useState<IOfferOverview | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [pdfType, setPdfType] = useState<OfferPdfTypeEnum>(OfferPdfTypeEnum.BRAND);

    useEffect(() => {
        if (!show || !id) {
            return;
        }
        setOverview(null);
        setIsLoading(true);
        fetchOverview(id)
            .then(data => {
                setOverview(data);
                setPdfType(OfferPdfTypeEnum.BRAND);
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsLoading(false));
    }, [show, id]);

    const storedPdf = overview?.pdfs.find(pdf => pdf.type === pdfType);
    const contact = overview?.invoiceData.contact;
    const bank = overview?.invoiceData.bank;

    const downloadStored = () =>
        storedPdf && downloadFileRequest(storedPdfUrl(storedPdf.id), 'pdf', null, null, setIsDownloading);

    const downloadGenerated = () =>
        overview && downloadFileRequest(generatedPdfUrl(overview.id), 'pdf', null, { type: pdfType }, setIsDownloading);

    const pricing = overview?.pricing[pdfType === OfferPdfTypeEnum.BRAND ? 'brand' : 'influencer'];

    const renderRow = (item: IOfferPricingItem, isSubItem = false) => (
        <div
            key={`${item.name}-${isSubItem}`}
            className={`d-flex justify-content-between py-1 ${
                isSubItem ? 'text-muted fs-8 ps-4' : 'fw-bold border-top pt-2'
            }`}
        >
            <span>
                {item.name} <span className="ms-3">x {item.nbPosts}</span>
            </span>
            <span>{formatPrice(item.price)}</span>
        </div>
    );

    const renderTotalLine = (label: string, value: React.ReactNode, className = '') => (
        <div className={`d-flex justify-content-between mb-2 ${className}`}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable dialogClassName="offer-overview-modal">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-3">
                    {title} {id}
                    {overview && <span className={`status ${overview.status}`}>{overview.status}</span>}
                    {overview?.isAnnex && (
                        <span className="fs-7 fw-normal text-muted">
                            Amendment {overview.annexNumber} of contract {overview.annexOfId}
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {isLoading || !overview ? (
                    <div className="text-center py-10">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
                            <span className="fs-8 text-muted">Version</span>
                            <div style={{ width: 150 }}>
                                <SingleSelectDropdown
                                    placeholder="Version"
                                    clearable={false}
                                    options={[
                                        { value: OfferPdfTypeEnum.BRAND, label: 'Brand' },
                                        { value: OfferPdfTypeEnum.INFLUENCER, label: 'Influencer' },
                                    ]}
                                    selected={pdfType}
                                    onChange={value => setPdfType(value as OfferPdfTypeEnum)}
                                />
                            </div>
                        </div>
                        <Row>
                            <Col xs={12} xxl={4}>
                                <Row>
                                    <Col xs={6}>
                                        <Info label="Company">{overview.company.displayName}</Info>
                                    </Col>
                                    <Col xs={6}>
                                        <Info label="Influencer">
                                            {overview.influencer.displayName ??
                                                `${overview.influencer.firstName} ${overview.influencer.lastName}`}
                                        </Info>
                                    </Col>
                                    <Col xs={6}>
                                        <Info label="Sign date">
                                            {formatDate(overview.acceptedDate ?? overview.createdAt)}
                                        </Info>
                                    </Col>
                                    <Col xs={6}>
                                        <Info label="Campaign">
                                            {overview.campaign.id} - {overview.campaign.title}
                                        </Info>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6} xxl={12}>
                                        {!pricing?.isAddonsOnly && (
                                            <>
                                                <div className="fs-8 text-muted mb-1">Content</div>
                                                {pricing?.platforms.map(platform => (
                                                    <React.Fragment key={platform.name}>
                                                        {renderRow(platform)}
                                                        {platform.items.map(item => renderRow(item, true))}
                                                    </React.Fragment>
                                                ))}
                                                <div className="d-flex justify-content-between fw-bold border-top py-2">
                                                    <span>Total content</span>
                                                    <span>{formatPrice(pricing?.contentTotal)}</span>
                                                </div>
                                            </>
                                        )}
                                    </Col>
                                    <Col md={6} xxl={12}>
                                        <div className="fs-8 text-muted mb-1">Add-ons</div>
                                        {pricing?.addons.length ? (
                                            pricing.addons.map((addon, index) => (
                                                <div
                                                    key={`${addon.label}-${index}`}
                                                    className="d-flex justify-content-between align-items-start border-bottom py-2"
                                                >
                                                    <div>
                                                        <div>{addon.label}</div>
                                                        {getAddonDescription(addon) && (
                                                            <div className="fs-8 text-muted">
                                                                {getAddonDescription(addon)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span className="text-nowrap">{formatPrice(addon.price)}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-muted py-2">No add-ons</div>
                                        )}
                                        {!!pricing?.addons.length && (
                                            <div className="d-flex justify-content-between fw-bold py-2">
                                                <span>Total items</span>
                                                <span>{formatPrice(pricing?.addonsTotal)}</span>
                                            </div>
                                        )}
                                    </Col>
                                </Row>

                                <div className="bg-light rounded p-4 my-4">
                                    {pricing?.subtotal !== null &&
                                        renderTotalLine(
                                            pdfType === OfferPdfTypeEnum.BRAND
                                                ? `Subtotal (${pricing?.subtotalPercentage}%)`
                                                : 'Subtotal',
                                            formatPrice(pricing?.subtotal)
                                        )}
                                    {pricing?.platformFee &&
                                        renderTotalLine(
                                            `Platform fee (${pricing.platformFee.percentage}%)`,
                                            `+ ${formatPrice(pricing.platformFee.amount)}`
                                        )}
                                    {pricing?.influencerFee &&
                                        renderTotalLine(
                                            `${
                                                pricing.influencerFee.isConnectedClub
                                                    ? 'Connected club'
                                                    : 'Preferred payout'
                                            } (${pricing.influencerFee.percentage}%)`,
                                            `- ${formatPrice(pricing.influencerFee.amount)}`
                                        )}
                                    {renderTotalLine(
                                        pdfType === OfferPdfTypeEnum.BRAND ? 'Grand total (100%)' : 'Grand total',
                                        pricing?.isBarter ? 'Barter Deal' : formatPrice(pricing?.grandTotal),
                                        'fw-bold mb-0'
                                    )}
                                </div>

                                <div className="mb-4">
                                    <div className="mb-3">
                                        <div className="fs-8 text-muted mb-1">Additional legal terms</div>
                                        <div style={{ whiteSpace: 'pre-wrap' }}>{overview.legalTerms || '-'}</div>
                                    </div>
                                    <div>
                                        {overview.legalDocuments.map(document => (
                                            <a
                                                key={document.id}
                                                href={document.path}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="d-flex align-items-center justify-content-between border rounded p-3 mb-2 text-dark"
                                            >
                                                <span className="d-flex align-items-center gap-3">
                                                    <FaFilePdf className="text-danger" size={20} />
                                                    <span>
                                                        <div className="fs-7">{document.name}</div>
                                                        <div className="fs-8 text-muted">
                                                            uploaded at: {formatDate(document.createdAt)}
                                                        </div>
                                                    </span>
                                                </span>
                                                <FaDownload />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </Col>

                            <Col xs={12} xl={6} xxl={4}>
                                <h5 className="mb-1">Influencer invoice data</h5>
                                <div className="text-muted mb-4">
                                    {overview.invoiceData.isAgency
                                        ? `Invoiced through agency: ${overview.invoiceData.agency?.displayName} (ID ${overview.invoiceData.agency?.id})`
                                        : 'Independent influencer'}
                                </div>

                                <h6>Receiver</h6>
                                <Row>
                                    <Col md={6}>
                                        <ReadOnlyField label="First name" value={contact?.invoiceFirstName} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="Last name" value={contact?.invoiceLastName} />
                                    </Col>
                                </Row>
                                <ReadOnlyField label="Company name" value={contact?.companyName} />
                                <ReadOnlyField label="Company register" value={contact?.companyRegister} />
                                <Row>
                                    <Col md={6}>
                                        <ReadOnlyField label="Phone number" value={contact?.phone} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="Email" value={contact?.email} />
                                    </Col>
                                </Row>

                                <h6>Address</h6>
                                <ReadOnlyField label="Street, house number, apartment number" value={contact?.street} />
                                <Row>
                                    <Col md={6}>
                                        <ReadOnlyField label="City" value={contact?.city} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="Postal code" value={contact?.zip} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="Country" value={contact?.country} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="State" value={contact?.province} />
                                    </Col>
                                </Row>

                                <h6>Payment info</h6>
                                <Row>
                                    <Col md={6}>
                                        <ReadOnlyField label="Bank name" value={bank?.accountBankName} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="IBAN" value={bank?.accountIban} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="SWIFT" value={bank?.accountSwift} />
                                    </Col>
                                    <Col md={6}>
                                        <ReadOnlyField label="VAT" value={contact?.uidNumber} />
                                    </Col>
                                </Row>

                                <h6>Invoice data details</h6>
                                <div className="fs-7">Sole proprietor: {String(!!contact?.soleProprietor)}</div>
                                <div className="fs-7">Small business: {String(!!contact?.smallBusiness)}</div>
                            </Col>

                            <Col xs={12} xl={6} xxl={4}>
                                <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                    <h5 className="mb-0">PDF</h5>
                                </div>
                                <div className="d-flex gap-2 mb-3">
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={downloadStored}
                                        disabled={!storedPdf || isDownloading}
                                    >
                                        <FaDownload className="me-2" />
                                        Download stored
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={downloadGenerated}
                                        disabled={isDownloading}
                                    >
                                        <FaDownload className="me-2" />
                                        Download generated
                                    </Button>
                                    {isDownloading && (
                                        <Spinner animation="border" size="sm" className="align-self-center" />
                                    )}
                                </div>
                                {storedPdf ? (
                                    <>
                                        <div className="fs-8 text-muted mb-2">
                                            Stored {formatDate(storedPdf.createdAt)}
                                            {storedPdf.sentBy ? ` by ${storedPdf.sentBy.displayName}` : ''}
                                        </div>
                                        <iframe
                                            title="Stored PDF"
                                            src={storedPdf.path}
                                            className="w-100 border rounded"
                                            style={{ height: '85vh', minHeight: 700 }}
                                        />
                                    </>
                                ) : (
                                    <div
                                        className="d-flex align-items-center justify-content-center border rounded text-muted"
                                        style={{ height: '85vh', minHeight: 700 }}
                                    >
                                        No stored PDF for this version
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-primary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
